import {Component, OnInit, ViewChild} from '@angular/core';
import {
    CashSession,
    CashSessionService,
    CashSessionStatuses,
    CashSessionSummary,
    FeatureKeys,
    LoadingService,
    Organization,
    OrganizationService,
    PickupLocation,
    PickupLocationService,
    SubscriberComponent,
    TipOutTypes,
    User,
    UserService
} from 'brewbill-lib';
import {NavController} from '@ionic/angular';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {ActivatedRoute} from '@angular/router';

@Component({
    selector: 'bb-cash-sessions',
    templateUrl: './cash-sessions.component.html',
    styleUrls: ['./cash-sessions.component.scss'],
})
export class CashSessionsComponent extends SubscriberComponent implements OnInit {
    searchCriteria: any;
    organization: Organization;
    pickupLocation: PickupLocation;
    currentUser: User;
    startRoute = false;

    dataSource: MatTableDataSource<CashSessionSummary>;
    displayedColumns: string[] = ['startTime', 'startedByName', 'pickupLocationName', 'status', 'drawerCount'];
    @ViewChild(MatSort, {static: true}) sort: MatSort;

    featureKeys = FeatureKeys;
    tipOutTypes = TipOutTypes;

    constructor(
        private cashSessionService: CashSessionService,
        private organizationService: OrganizationService,
        private pickupLocationService: PickupLocationService,
        private userService: UserService,
        private loadingService: LoadingService,
        private navController: NavController,
        private route: ActivatedRoute
    ) {
        super();
    }

    ngOnInit() {
        this.organization = this.organizationService.currentValue;
        this.pickupLocation = this.pickupLocationService.currentValue;

        if (this.organization.tipOutType !== TipOutTypes.PAY_PERIOD_POOLED) {
            this.displayedColumns.push('remainingTips');
        }

        if (!!this.route.queryParams) {
            this.startRoute = this.route.snapshot.queryParams.start;
        }

        this.searchCriteria = this.cashSessionService.getSearchCriteria();
        if (this.organization.pickupLocations.length > 1 && !this.searchCriteria.pickupLocations) {
            if (!!this.pickupLocation) {
                this.searchCriteria.pickupLocations = [this.pickupLocation.id];
            } else {
                const idString = localStorage.getItem('ACTIVE_PICKUP_LOCATION');
                const id = !!idString ? Number.parseInt(idString, 10) : null;
                if (!!id && this.organization.pickupLocations.some(s => s.id === id)) {
                    this.searchCriteria.pickupLocations = [id];
                } else {
                    this.searchCriteria.pickupLocations = this.organization.pickupLocations.map(p => p.id);
                }
            }
        }

        this.dataSource = new MatTableDataSource([]);

        this.subscribe(this.userService.current.subscribe(u => this.currentUser = !!u ? new User(u) : null));

        this.subscribe(this.organizationService.current.subscribe(o => {
            this.organization = !!o ? new Organization(o) : null;
        }));

        this.subscribe(this.cashSessionService.event.subscribe(c => {
            if (!!c) {
                this.refreshSummaries();
            }
        }));

        this.subscribe(this.pickupLocationService.current.subscribe(p => {
            this.pickupLocation = !!p ? new PickupLocation(p) : null;
            if (!!this.pickupLocation) {
                this.searchCriteria.pickupLocations = [this.pickupLocation.id];
            }
            this.refreshSummaries();
        }));

        this.refreshSummaries();
    }

    refreshSummaries() {
        if (!!this.currentUser && !!this.organization
            && this.currentUser.authorizedTo(FeatureKeys.CASH_SESSION_VIEW_KEYS, this.organization.id)) {
            this.loadingService.present();
            this.cashSessionService.setSearchCriteria(this.searchCriteria);

            this.cashSessionService.getDateRangeSummaries(this.organization.id,
                !!this.searchCriteria.pickupLocations && this.searchCriteria.pickupLocations.length > 0
                    ? this.searchCriteria.pickupLocations
                    : this.organization.pickupLocations.map(p => p.id),
                this.searchCriteria.startDate, this.searchCriteria.endDate)
                .subscribe(async (c: CashSessionSummary[]) => {
                    const summaries = c.sort((a: CashSessionSummary, b: CashSessionSummary) => {
                        if (a.status === CashSessionStatuses.ACTIVE && b.status !== CashSessionStatuses.ACTIVE) {
                            return -1;
                        } else if (a.status === CashSessionStatuses.PENDING
                            && (b.status !== CashSessionStatuses.ACTIVE || b.status !== CashSessionStatuses.PENDING)) {
                            return -1;
                        } else {
                            return 0;
                        }
                    });

                    this.dataSource = new MatTableDataSource(summaries);
                    this.dataSource.sort = this.sort;
                    this.loadingService.dismiss();
                });
        }
    }

    async displayCashSession(cashSession: CashSession) {
        await this.navController.navigateForward(`/${this.organization.id}/cash-session/${cashSession.id}`);
    }

    async displayReport() {
        await this.navController.navigateForward(`/${this.organization.id}/employee-tip-report`);
    }

    protected readonly CashSessionStatuses = CashSessionStatuses;
}
